// Explore-stats page: Overseas born.
// Recreates the Handbook page — a world map with a bubble per country of
// birth sized by the number of parliamentarians born there, filterable by
// Parliament (default: current Parliament). Supporting table + CSV.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, FormControl, InputLabel, Select, MenuItem, Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import COUNTRY_CENTROIDS from './countryCentroids';
import { basemapFor } from '../../theme';

export default function OverseasBorn() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const basemap = basemapFor(theme.palette.mode);
  const bubbleColor = isDark ? '#64b5f6' : '#1565C0';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parliament, setParliament] = useState('current');

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getExploreStat('overseas-born',
          parliament === 'current' ? {} : { parliament });
        if (active) { setData(res); setError(null); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [parliament]);

  const countries = data?.charts?.countries || [];
  const parliaments = data?.charts?.parliaments || [];
  const maxCount = countries.reduce((m, c) => Math.max(m, c.count), 0) || 1;

  return (
    <ExploreStatsPage
      title="Overseas born"
      description="Parliamentarians born overseas, by country of birth. Early Parliaments show a heavy concentration in the United Kingdom; recent Parliaments are far more diverse."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="overseas-born"
    >
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Parliament</InputLabel>
          <Select
            label="Parliament"
            value={parliament}
            onChange={(e) => setParliament(e.target.value)}
          >
            <MenuItem value="current">Current Parliament</MenuItem>
            {parliaments.map((p) => (
              <MenuItem key={p} value={p}>{`${p}${
                ['th', 'st', 'nd', 'rd'][(p % 100 - p % 10 === 10 || p % 10 > 3) ? 0 : p % 10]
              } Parliament`}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {data && (
        <Paper elevation={1} sx={{ p: 1, mb: 3, borderRadius: 3, overflow: 'hidden' }}>
          <MapContainer center={[20, 10]} zoom={2} scrollWheelZoom={false}
                        style={{ height: 460, width: '100%' }}
                        worldCopyJump>
            <TileLayer
              key={basemap.url}
              attribution={basemap.attribution}
              url={basemap.url}
              subdomains={basemap.subdomains || 'abc'}
            />
            {countries.map((c) => {
              const pos = COUNTRY_CENTROIDS[c.country];
              if (!pos) return null;
              const radius = 6 + 26 * Math.sqrt(c.count / maxCount);
              return (
                <CircleMarker key={c.country} center={pos} radius={radius}
                              pathOptions={{ color: bubbleColor, fillColor: bubbleColor, fillOpacity: 0.55 }}>
                  <Tooltip>{`${c.country}: ${c.count}`}</Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            Bubble size ∝ number of parliamentarians born in that country.
          </Typography>
        </Paper>
      )}
    </ExploreStatsPage>
  );
}
